import Alpine    from 'alpinejs';
import SimpleBar from 'simplebar';

import ExperienceMap        from './components/ExperienceMap.js';
import ViewportManager      from './components/ViewportManager.js';
import SlidesManager        from './components/SlidesManager.js';
import ScrollModeManager    from './components/ScrollModeManager.js';
import NavigationController from './components/NavigationController.js';
import VideoExperience      from './components/VideoExperience.js';
import PreloaderController  from './components/PreloaderController.js';
import DOMCache             from './components/DOMCache.js';

import global                from './alpine/global.js';
import experienceSectionQuiz from './alpine/experienceQuiz.js';

if (typeof window !== 'undefined') {
    window.SimpleBar = SimpleBar;
}

const DEBUG_SKIP_TO_SLIDES = false;

let viewportManager   = null;
let slidesManager     = null;
let scrollModeManager = null;
const domCache        = new DOMCache();

/**
 * Initialize the entire experience application.
 */
function init() { // Initialize viewport height manager
    viewportManager = new ViewportManager();
    viewportManager.init();

    // Setup Alpine.js
    window.Alpine = Alpine;
    Alpine.data('global', global);
    Alpine.data('experienceSectionQuiz', experienceSectionQuiz);
    Alpine.start();

    // Get DOM elements using cache
    const preloaderElement       = domCache.getElementById('experience-preloader');
    const progressTextElement    = domCache.querySelector('[data-preloader-progress]');
    const heroShellElement       = domCache.querySelector('.experience-hero__video-shell');
    const heroOverlayElement     = domCache.querySelector('.experience-hero__overlay');
    const startButtonElement     = heroOverlayElement ? domCache.findInParent(
        heroOverlayElement,
        '[data-experience-start]',
        'startButton'
    ) : null;
    const centerButtonElement    = heroOverlayElement ? domCache.findInParent(
        heroOverlayElement,
        '[data-experience-center]',
        'centerButton'
    ) : null;
    const continueButtonElements = heroOverlayElement ? domCache.findAllInParent(
        heroOverlayElement,
        '[data-experience-complete]',
        'continueButtons'
    ) : null;
    const scrollSectionElement   = domCache.querySelector('.experience__scroll-section');
    const videoStageElement      = domCache.getElementById('experience-video-stage');
    const experienceMapElements  = domCache.querySelectorAll('[data-experience-map]');
    const experienceRootElement  = domCache.querySelector('.experience');

    // Initialize experience maps for all sections
    if (experienceMapElements && experienceMapElements.length > 0) {
        experienceMapElements.forEach((element) => {
            const sectionId = element.getAttribute('data-experience-map');

            if (!sectionId) {
                return;
            }

            const experienceMap = new ExperienceMap({
                rootElement : element,
                sectionId
            });

            experienceMap.init();
        });
    }

    // Initialize main experience flow
    if (preloaderElement && progressTextElement && heroShellElement) {
        // Create video experience instance
        const videoExperience = new VideoExperience({
            shellElement   : heroShellElement,
            overlayElement : heroOverlayElement
        });

        videoExperience.startSnow();

        // Create scroll mode manager
        scrollModeManager = new ScrollModeManager({
            scrollSectionElement,
            videoStageElement,
            experienceRootElement,
            videoExperience
        });

        // Create navigation controller
        const navigationController = new NavigationController({
            videoExperience,
            enterScrollMode : () => {
                if (!slidesManager) {
                    slidesManager = new SlidesManager({
                        scrollSectionElement,
                        navigationController
                    });

                    slidesManager.init();
                }

                scrollModeManager.enterScrollMode();
            },
            exitScrollModeToVideoPhaseTwo : () => {
                scrollModeManager.exitScrollMode(() => {
                    if (slidesManager) {
                        slidesManager.destroy();
                        slidesManager = null;
                    }
                });
            },
            initialPhase            : 'preloader',
            allowBackwardNavigation : false
        });

        // Attach button event listeners
        if (startButtonElement) {
            startButtonElement.addEventListener('click', (event) => {
                event.preventDefault();
                navigationController.navigate(1);
            });
        }

        if (centerButtonElement) {
            centerButtonElement.addEventListener('click', (event) => {
                event.preventDefault();
                navigationController.navigate(2);
            });
        }

        if (continueButtonElements && continueButtonElements.length > 0) {
            continueButtonElements.forEach((buttonElement) => {
                buttonElement.addEventListener('click', (event) => {
                    event.preventDefault();
                    navigationController.navigate(1);
                });
            });
        }

        // Initialize preloader controller
        const controller = new PreloaderController({
            videoUrl              : 'video/video-story.mp4',
            preloaderElement,
            progressTextElement,
            videoContainerElement : heroShellElement,
            videoElement          : null,
            onVideoReady(blobUrl) {
                videoExperience.prepare(blobUrl);
            },
            onComplete() {
                if (DEBUG_SKIP_TO_SLIDES) {
                    // Debug mode: skip video and go directly to slides
                    // videoExperience.hasStageThreeShown = true;
                    navigationController.setPhase('scroll-mode');

                    if (!slidesManager) {
                        slidesManager = new SlidesManager({
                            scrollSectionElement,
                            navigationController
                        });

                        slidesManager.init();
                    }

                    scrollModeManager.enterScrollMode();

                    return;
                }

                // Normal mode: start video intro
                videoExperience.startIntro();
                navigationController.setPhase('video-phase-1');
            }
        });

        controller.init();

        // Handle video playback failure (iOS low power mode, autoplay restrictions)
        window.addEventListener('video-playback-failed', (event) => {
            // eslint-disable-next-line no-console
            console.warn('[Experience] Video playback failed, automatically switching to slides mode', event.detail);

            // Hide header when skipping to slides
            const headerElement = document.querySelector('.experience-header');
            if (headerElement) {
                headerElement.classList.add('experience-header--is-hidden');
            }

            // Skip to slides mode
            navigationController.setPhase('scroll-mode');

            if (!slidesManager) {
                slidesManager = new SlidesManager({
                    scrollSectionElement,
                    navigationController
                });

                slidesManager.init();
            }

            scrollModeManager.enterScrollMode();
        });
    }

    Array.prototype.forEach.call(domCache.querySelectorAll('[data-simplebar]'), (el) => new SimpleBar(el, {
        autoHide         : false,
        scrollbarMinSize : 6,
        ariaLabel        : 'прокручиваемый контент'
    }));
}

// Start application when DOM is ready
(function () {
    init();
}());
